/**
 * Backend Value Converter
 *
 * Converts display values (from PDF extraction) to API-compatible format for backend submission.
 * Also provides reverse conversion for ML predictions (codes to display text).
 *
 * Flow:
 * - PDF Extraction: Returns original text ("Germany", "10.01.2025")
 * - Form Display: Shows original text as-is
 * - Backend Submit: Converts to API format ("DE", "20250110")
 * - ML Predictions: Converts codes to display text for suggestions
 */

// ============================================================================
// COUNTRY CODE MAPPINGS (ISO 3166-1 alpha-2)
// ============================================================================

/** Country name to ISO 2-letter code mapping (case-insensitive lookup) */
export const COUNTRY_NAME_TO_CODE: Record<string, string> = {
  // A
  'afghanistan': 'AF', 'albania': 'AL', 'algeria': 'DZ', 'andorra': 'AD',
  'angola': 'AO', 'argentina': 'AR', 'armenia': 'AM', 'australia': 'AU',
  'austria': 'AT', 'azerbaidjan': 'AZ', 'azerbaijan': 'AZ',
  // B
  'bahrain': 'BH', 'bangladesh': 'BD', 'belarus': 'BY', 'belgium': 'BE',
  'bolivia': 'BO', 'bosnia': 'BA', 'bosnia and herzegovina': 'BA',
  'brazil': 'BR', 'brasil': 'BR', 'brunei': 'BN', 'bulgaria': 'BG',
  // C
  'cambodia': 'KH', 'cameroon': 'CM', 'canada': 'CA', 'chile': 'CL',
  'china': 'CN', 'colombia': 'CO', 'costa rica': 'CR', 'croatia': 'HR',
  'cuba': 'CU', 'cyprus': 'CY', 'czech republic': 'CZ', 'czechia': 'CZ',
  // D
  'denmark': 'DK', 'dominican republic': 'DO',
  // E
  'ecuador': 'EC', 'egypt': 'EG', 'el salvador': 'SV', 'estonia': 'EE',
  'ethiopia': 'ET',
  // F
  'finland': 'FI', 'france': 'FR',
  // G
  'georgia': 'GE', 'germany': 'DE', 'ghana': 'GH', 'greece': 'GR',
  'guatemala': 'GT',
  // H
  'honduras': 'HN', 'hong kong': 'HK', 'hungary': 'HU',
  // I
  'iceland': 'IS', 'india': 'IN', 'indonesia': 'ID', 'iran': 'IR',
  'iraq': 'IQ', 'ireland': 'IE', 'israel': 'IL', 'italy': 'IT',
  // J
  'jamaica': 'JM', 'japan': 'JP', 'jordan': 'JO',
  // K
  'kazakhstan': 'KZ', 'kenya': 'KE', 'korea': 'KR', 'south korea': 'KR',
  'north korea': 'KP', 'kuwait': 'KW', 'kyrgyzstan': 'KG',
  // L
  'latvia': 'LV', 'lebanon': 'LB', 'libya': 'LY', 'liechtenstein': 'LI',
  'lithuania': 'LT', 'luxembourg': 'LU',
  // M
  'malaysia': 'MY', 'maldives': 'MV', 'malta': 'MT', 'mexico': 'MX',
  'moldova': 'MD', 'monaco': 'MC', 'mongolia': 'MN', 'montenegro': 'ME',
  'morocco': 'MA', 'myanmar': 'MM',
  // N
  'nepal': 'NP', 'netherlands': 'NL', 'holland': 'NL', 'new zealand': 'NZ',
  'nicaragua': 'NI', 'nigeria': 'NG', 'norway': 'NO',
  // O
  'oman': 'OM',
  // P
  'pakistan': 'PK', 'panama': 'PA', 'paraguay': 'PY', 'peru': 'PE',
  'philippines': 'PH', 'poland': 'PL', 'portugal': 'PT',
  // Q
  'qatar': 'QA',
  // R
  'romania': 'RO', 'russia': 'RU', 'russian federation': 'RU', 'rwanda': 'RW',
  // S
  'saudi arabia': 'SA', 'senegal': 'SN', 'serbia': 'RS', 'singapore': 'SG',
  'slovakia': 'SK', 'slovenia': 'SI', 'south africa': 'ZA', 'spain': 'ES',
  'sri lanka': 'LK', 'sweden': 'SE', 'switzerland': 'CH', 'syria': 'SY',
  // T
  'taiwan': 'TW', 'tajikistan': 'TJ', 'tanzania': 'TZ', 'thailand': 'TH',
  'tunisia': 'TN', 'turkey': 'TR', 'turkmenistan': 'TM',
  // U
  'uganda': 'UG', 'ukraine': 'UA', 'united arab emirates': 'AE', 'uae': 'AE',
  'united kingdom': 'GB', 'uk': 'GB', 'great britain': 'GB', 'britain': 'GB',
  'england': 'GB', 'united states': 'US', 'usa': 'US', 'united states of america': 'US',
  'america': 'US', 'uruguay': 'UY', 'uzbekistan': 'UZ',
  // V
  'venezuela': 'VE', 'vietnam': 'VN', 'viet nam': 'VN',
  // Y
  'yemen': 'YE',
  // Z
  'zambia': 'ZM', 'zimbabwe': 'ZW',

  // German names
  'deutschland': 'DE', 'frankreich': 'FR', 'spanien': 'ES', 'italien': 'IT',
  'niederlande': 'NL', 'belgien': 'BE', 'österreich': 'AT', 'osterreich': 'AT',
  'schweiz': 'CH', 'polen': 'PL', 'tschechien': 'CZ', 'ungarn': 'HU',
  'griechenland': 'GR', 'türkei': 'TR', 'turkei': 'TR', 'russland': 'RU',
  'indien': 'IN', 'brasilien': 'BR',
  'vereinigte staaten': 'US', 'vereinigtes königreich': 'GB',
  'grossbritannien': 'GB', 'großbritannien': 'GB',
};

/** ISO 2-letter code to country name mapping (for display) */
export const COUNTRY_CODE_TO_NAME: Record<string, string> = {
  'AF': 'Afghanistan', 'AL': 'Albania', 'DZ': 'Algeria', 'AD': 'Andorra',
  'AO': 'Angola', 'AR': 'Argentina', 'AM': 'Armenia', 'AU': 'Australia',
  'AT': 'Austria', 'AZ': 'Azerbaijan',
  'BH': 'Bahrain', 'BD': 'Bangladesh', 'BY': 'Belarus', 'BE': 'Belgium',
  'BO': 'Bolivia', 'BA': 'Bosnia and Herzegovina', 'BR': 'Brazil',
  'BN': 'Brunei', 'BG': 'Bulgaria',
  'KH': 'Cambodia', 'CM': 'Cameroon', 'CA': 'Canada', 'CL': 'Chile',
  'CN': 'China', 'CO': 'Colombia', 'CR': 'Costa Rica', 'HR': 'Croatia',
  'CU': 'Cuba', 'CY': 'Cyprus', 'CZ': 'Czech Republic',
  'DK': 'Denmark', 'DO': 'Dominican Republic',
  'EC': 'Ecuador', 'EG': 'Egypt', 'SV': 'El Salvador', 'EE': 'Estonia',
  'ET': 'Ethiopia',
  'FI': 'Finland', 'FR': 'France',
  'GE': 'Georgia', 'DE': 'Germany', 'GH': 'Ghana', 'GR': 'Greece',
  'GT': 'Guatemala',
  'HN': 'Honduras', 'HK': 'Hong Kong', 'HU': 'Hungary',
  'IS': 'Iceland', 'IN': 'India', 'ID': 'Indonesia', 'IR': 'Iran',
  'IQ': 'Iraq', 'IE': 'Ireland', 'IL': 'Israel', 'IT': 'Italy',
  'JM': 'Jamaica', 'JP': 'Japan', 'JO': 'Jordan',
  'KZ': 'Kazakhstan', 'KE': 'Kenya', 'KR': 'South Korea', 'KP': 'North Korea',
  'KW': 'Kuwait', 'KG': 'Kyrgyzstan',
  'LV': 'Latvia', 'LB': 'Lebanon', 'LY': 'Libya', 'LI': 'Liechtenstein',
  'LT': 'Lithuania', 'LU': 'Luxembourg',
  'MY': 'Malaysia', 'MV': 'Maldives', 'MT': 'Malta', 'MX': 'Mexico',
  'MD': 'Moldova', 'MC': 'Monaco', 'MN': 'Mongolia', 'ME': 'Montenegro',
  'MA': 'Morocco', 'MM': 'Myanmar',
  'NP': 'Nepal', 'NL': 'Netherlands', 'NZ': 'New Zealand', 'NI': 'Nicaragua',
  'NG': 'Nigeria', 'NO': 'Norway',
  'OM': 'Oman',
  'PK': 'Pakistan', 'PA': 'Panama', 'PY': 'Paraguay', 'PE': 'Peru',
  'PH': 'Philippines', 'PL': 'Poland', 'PT': 'Portugal',
  'QA': 'Qatar',
  'RO': 'Romania', 'RU': 'Russia', 'RW': 'Rwanda',
  'SA': 'Saudi Arabia', 'SN': 'Senegal', 'RS': 'Serbia', 'SG': 'Singapore',
  'SK': 'Slovakia', 'SI': 'Slovenia', 'ZA': 'South Africa', 'ES': 'Spain',
  'LK': 'Sri Lanka', 'SE': 'Sweden', 'CH': 'Switzerland', 'SY': 'Syria',
  'TW': 'Taiwan', 'TJ': 'Tajikistan', 'TZ': 'Tanzania', 'TH': 'Thailand',
  'TN': 'Tunisia', 'TR': 'Turkey', 'TM': 'Turkmenistan',
  'UG': 'Uganda', 'UA': 'Ukraine', 'AE': 'United Arab Emirates',
  'GB': 'United Kingdom', 'US': 'United States', 'UY': 'Uruguay', 'UZ': 'Uzbekistan',
  'VE': 'Venezuela', 'VN': 'Vietnam',
  'YE': 'Yemen',
  'ZM': 'Zambia', 'ZW': 'Zimbabwe',
};

// ============================================================================
// FIELD PATTERN DETECTION
// ============================================================================

/** Patterns to identify country fields */
const COUNTRY_FIELD_PATTERNS = [
  /\.land$/i,              // kopf.empfanger.land, kopf.ausfuhrer.land
  /land$/i,                // ausfuhrland, bestimmungsLand
  /country/i,              // any field with "country"
  /staatszugehorigkeit/i,  // nationality fields
];

/** Patterns to identify date fields (8-digit YYYYMMDD) */
const DATE_FIELD_PATTERNS = [
  /datum$/i,               // rechnungsdatum, lieferdatum
  /date$/i,                // invoiceDate
];

/** Patterns to identify timestamp fields (14-digit YYYYMMDDHHmmss) */
const TIMESTAMP_FIELD_PATTERNS = [
  /zeitpunkt/i,            // zeitpunktderAnmeldung
];

// ============================================================================
// CONVERSION FUNCTIONS (Display Value -> Backend Value)
// ============================================================================

/**
 * Convert country name to ISO 2-letter code
 * @param countryName - Country name (e.g., "Germany", "deutschland")
 * @returns ISO code (e.g., "DE") or original value if not found
 */
export function convertCountryToCode(countryName: string): string {
  if (!countryName || typeof countryName !== 'string') return countryName;

  const trimmed = countryName.trim();

  // Already a valid 2-letter code
  if (/^[A-Z]{2}$/.test(trimmed)) {
    return trimmed;
  }

  // Lookup in mapping (case-insensitive)
  const code = COUNTRY_NAME_TO_CODE[trimmed.toLowerCase()];
  if (code) {
    return code;
  }

  // Try uppercase (might already be a code)
  const upper = trimmed.toUpperCase();
  if (COUNTRY_CODE_TO_NAME[upper]) {
    return upper;
  }

  // Return original if no mapping found
  return trimmed;
}

/**
 * Convert ISO country code to display name
 * @param code - ISO 2-letter code (e.g., "DE")
 * @returns Country name (e.g., "Germany") or original code if not found
 */
export function convertCodeToCountry(code: string): string {
  if (!code || typeof code !== 'string') return code;

  const trimmed = code.trim().toUpperCase();
  return COUNTRY_CODE_TO_NAME[trimmed] || code;
}

/**
 * Convert date string to ATLAS format (YYYYMMDD)
 * Supports multiple input formats:
 * - ISO: 2025-01-10 or 2025-01-10T12:00:00
 * - European: 10.01.2025, 10/01/2025, 10.01.2025 12:00:00
 * - Dashes: 10-01-2025
 * - Already formatted: 20250110 or 20250110120000
 *
 * @param dateStr - Date string in various formats
 * @returns YYYYMMDD format or original value if parsing fails
 */
export function convertDateToAtlasFormat(dateStr: string): string {
  if (!dateStr || typeof dateStr !== 'string') return dateStr;

  const trimmed = dateStr.trim();

  // Already in YYYYMMDD format (8 digits)
  if (/^\d{8}$/.test(trimmed)) {
    return trimmed;
  }

  // Already in YYYYMMDDHHmmss format (14 digits) - extract date part
  if (/^\d{14}$/.test(trimmed)) {
    return trimmed.substring(0, 8);
  }

  // European with time: DD.MM.YYYY HH:mm:ss — strip time, then convert
  const euTimeMatch = trimmed.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})\s+\d{2}:\d{2}:\d{2}$/);
  if (euTimeMatch) {
    const day = euTimeMatch[1].padStart(2, '0');
    const month = euTimeMatch[2].padStart(2, '0');
    const year = euTimeMatch[3];
    return `${year}${month}${day}`;
  }

  // ISO datetime: 2025-01-10T12:00:00 or 2025-01-10 12:00:00 — strip time
  const isoTimeMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})[T\s]\d{2}:\d{2}/);
  if (isoTimeMatch) {
    return `${isoTimeMatch[1]}${isoTimeMatch[2]}${isoTimeMatch[3]}`;
  }

  // Try parsing various formats
  let parsed: Date | null = null;

  // ISO format: 2025-01-10
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    parsed = new Date(trimmed);
  }
  // European format: 10.01.2025 or 10/01/2025
  else if (/^\d{1,2}[.\/]\d{1,2}[.\/]\d{4}$/.test(trimmed)) {
    const parts = trimmed.split(/[.\/]/);
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    // Validate day/month ranges
    if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
      parsed = new Date(year, month - 1, day);
    }
  }
  // Dashes format: DD-MM-YYYY or MM-DD-YYYY
  else if (/^\d{1,2}[-\/]\d{1,2}[-\/]\d{4}$/.test(trimmed)) {
    const parts = trimmed.split(/[-\/]/);
    const first = parseInt(parts[0], 10);
    const second = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    // Determine if month-first (US) or day-first (EU) based on values
    if (first > 12 && second <= 12) {
      // Day first (EU): 15-01-2025
      parsed = new Date(year, second - 1, first);
    } else if (first <= 12) {
      // Month first (US): 01-15-2025
      parsed = new Date(year, first - 1, second);
    }
  }

  // Format to YYYYMMDD if parsing succeeded
  if (parsed && !isNaN(parsed.getTime())) {
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  }

  // Return original if parsing failed
  return trimmed;
}

/**
 * Convert timestamp string to ATLAS format (YYYYMMDDHHmmss)
 * @param timestampStr - Timestamp string
 * @returns YYYYMMDDHHmmss format or original value
 */
export function convertTimestampToAtlasFormat(timestampStr: string): string {
  if (!timestampStr || typeof timestampStr !== 'string') return timestampStr;

  const trimmed = timestampStr.trim();

  // Already in 14-digit format
  if (/^\d{14}$/.test(trimmed)) {
    return trimmed;
  }

  // 8-digit date - append default time (noon)
  if (/^\d{8}$/.test(trimmed)) {
    return `${trimmed}120000`;
  }

  // ISO datetime: 2025-01-10T12:00:00 or 2025-01-10 12:00:00
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2}):(\d{2})/);
  if (isoMatch) {
    const [, year, month, day, hour, min, sec] = isoMatch;
    return `${year}${month}${day}${hour}${min}${sec}`;
  }

  // European display format: DD.MM.YYYY HH:mm:ss (from formatAtlasTimestampForDisplay)
  const euMatch = trimmed.match(/^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/);
  if (euMatch) {
    const [, day, month, year, hour, min, sec] = euMatch;
    return `${year}${month}${day}${hour}${min}${sec}`;
  }

  // Try to parse as date and use noon
  const dateOnly = convertDateToAtlasFormat(trimmed);
  if (/^\d{8}$/.test(dateOnly)) {
    return `${dateOnly}120000`;
  }

  return trimmed;
}

/**
 * Format ATLAS date to German display format (DD.MM.YYYY / TT.MM.JJJJ)
 * ATLAS validation expects TT.MM.JJJJ (DD.MM.YYYY) — date only, no time.
 * @param atlasDate - Date in various formats (YYYYMMDD, YYYY-MM-DD, DD-MM-YYYY, etc.)
 * @returns German format date string (DD.MM.YYYY)
 */
export function formatAtlasDateForDisplay(atlasDate: string): string {
  if (!atlasDate || typeof atlasDate !== 'string') return atlasDate;

  const trimmed = atlasDate.trim();

  // Already in German format (DD.MM.YYYY)
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(trimmed)) {
    return trimmed;
  }

  // DD.MM.YYYY HH:mm:ss — strip time part
  const euWithTime = trimmed.match(/^(\d{2}\.\d{2}\.\d{4})\s+\d{2}:\d{2}:\d{2}$/);
  if (euWithTime) {
    return euWithTime[1];
  }

  // DD-MM-YYYY (dashes) — convert to dots
  if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
    return trimmed.replace(/-/g, '.');
  }

  // Convert ISO format YYYY-MM-DD to DD.MM.YYYY
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const [year, month, day] = trimmed.split('-');
    return `${day}.${month}.${year}`;
  }

  // ISO datetime YYYY-MM-DDT... or YYYY-MM-DD HH:... — strip time, reformat
  const isoWithTime = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})[T\s]\d{2}:\d{2}/);
  if (isoWithTime) {
    return `${isoWithTime[3]}.${isoWithTime[2]}.${isoWithTime[1]}`;
  }

  // Convert YYYYMMDD to DD.MM.YYYY
  if (/^\d{8}$/.test(trimmed)) {
    return `${trimmed.substring(6, 8)}.${trimmed.substring(4, 6)}.${trimmed.substring(0, 4)}`;
  }

  // Handle 14-digit timestamp — extract and format date only
  if (/^\d{14}$/.test(trimmed)) {
    return `${trimmed.substring(6, 8)}.${trimmed.substring(4, 6)}.${trimmed.substring(0, 4)}`;
  }

  // Try to parse other formats
  const date = new Date(trimmed);
  if (!isNaN(date.getTime())) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  return atlasDate;
}

/**
 * Format ATLAS timestamp (YYYYMMDDHHmmss) to German display format
 * ATLAS validation (gRPC) expects TT.MM.JJJJ (DD.MM.YYYY) — date only, no time.
 * Display matches the required format so users see what ATLAS expects.
 * @param atlasTimestamp - Timestamp in YYYYMMDDHHmmss format
 * @returns German format date string (DD.MM.YYYY) — time stripped
 */
export function formatAtlasTimestampForDisplay(atlasTimestamp: string): string {
  if (!atlasTimestamp || typeof atlasTimestamp !== 'string') return atlasTimestamp;

  const trimmed = atlasTimestamp.trim();

  // Convert YYYYMMDDHHmmss to DD.MM.YYYY (strip time — ATLAS expects date only)
  if (/^\d{14}$/.test(trimmed)) {
    return `${trimmed.substring(6, 8)}.${trimmed.substring(4, 6)}.${trimmed.substring(0, 4)}`;
  }

  // 8-digit - format as date
  if (/^\d{8}$/.test(trimmed)) {
    return formatAtlasDateForDisplay(trimmed);
  }

  // Already DD.MM.YYYY HH:mm:ss — strip time part
  const euWithTime = trimmed.match(/^(\d{2}\.\d{2}\.\d{4})\s+\d{2}:\d{2}:\d{2}$/);
  if (euWithTime) {
    return euWithTime[1];
  }

  // Already DD.MM.YYYY — pass through
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(trimmed)) {
    return trimmed;
  }

  return atlasTimestamp;
}

// ============================================================================
// FIELD TYPE DETECTION
// ============================================================================

export type FieldType = 'country' | 'date' | 'timestamp' | 'default';

/**
 * Detect the type of field based on field path
 * @param fieldPath - The field path (e.g., "kopf.empfanger.land")
 * @returns Field type for conversion
 */
export function detectFieldType(fieldPath: string): FieldType {
  if (!fieldPath) return 'default';

  if (COUNTRY_FIELD_PATTERNS.some(p => p.test(fieldPath))) {
    return 'country';
  }
  if (TIMESTAMP_FIELD_PATTERNS.some(p => p.test(fieldPath))) {
    return 'timestamp';
  }
  if (DATE_FIELD_PATTERNS.some(p => p.test(fieldPath))) {
    return 'date';
  }
  return 'default';
}

/**
 * Check if a field is a country field
 */
export function isCountryField(fieldPath: string): boolean {
  return detectFieldType(fieldPath) === 'country';
}

/**
 * Check if a field is a date field
 */
export function isDateField(fieldPath: string): boolean {
  return detectFieldType(fieldPath) === 'date';
}

/**
 * Check if a field is a timestamp field
 */
export function isTimestampField(fieldPath: string): boolean {
  return detectFieldType(fieldPath) === 'timestamp';
}

// ============================================================================
// MASTER CONVERSION FUNCTIONS
// ============================================================================

/**
 * Convert a display value to backend API format based on field path
 * @param fieldPath - The field path (e.g., "kopf.empfanger.land")
 * @param value - The display value (e.g., "Germany")
 * @returns Backend API value (e.g., "DE")
 */
export function convertToBackendValue(fieldPath: string, value: string): string {
  if (!value || typeof value !== 'string') return value;

  const fieldType = detectFieldType(fieldPath);

  switch (fieldType) {
    case 'country':
      return convertCountryToCode(value);
    case 'timestamp':
      return convertTimestampToAtlasFormat(value);
    case 'date':
      return convertDateToAtlasFormat(value);
    default:
      return value;
  }
}

/**
 * Convert a backend/ML value to display format based on field path
 * Used for showing ML predictions in human-readable format
 * @param fieldPath - The field path (e.g., "kopf.empfanger.land")
 * @param value - The backend/ML value (e.g., "DE")
 * @returns Display value (e.g., "Germany")
 */
export function convertToDisplayValue(fieldPath: string, value: string): string {
  if (!value || typeof value !== 'string') return value;

  const fieldType = detectFieldType(fieldPath);

  switch (fieldType) {
    case 'country':
      return convertCodeToCountry(value);
    case 'timestamp':
      return formatAtlasTimestampForDisplay(value);
    case 'date':
      return formatAtlasDateForDisplay(value);
    default:
      return value;
  }
}

/**
 * Convert entire extracted data object for backend submission
 * Recursively processes all fields and converts values as needed
 * @param data - The extracted data object
 * @param parentPath - Parent path for nested objects (used internally)
 * @returns New object with converted values
 */
export function convertDataForBackend(data: any, parentPath: string = ''): any {
  if (data === null || data === undefined) return data;

  if (Array.isArray(data)) {
    return data.map((item, index) => {
      const arrayPath = parentPath ? `${parentPath}[${index}]` : `[${index}]`;
      return convertDataForBackend(item, arrayPath);
    });
  }

  if (typeof data === 'object') {
    const result: Record<string, any> = {};
    for (const [key, value] of Object.entries(data)) {
      const fieldPath = parentPath ? `${parentPath}.${key}` : key;

      if (typeof value === 'string') {
        result[key] = convertToBackendValue(fieldPath, value);
      } else {
        result[key] = convertDataForBackend(value, fieldPath);
      }
    }
    return result;
  }

  // Primitive value at root level
  if (typeof data === 'string') {
    return convertToBackendValue(parentPath, data);
  }

  return data;
}

// ============================================================================
// UTILITY EXPORTS
// ============================================================================

export default {
  // Country conversions
  convertCountryToCode,
  convertCodeToCountry,
  COUNTRY_NAME_TO_CODE,
  COUNTRY_CODE_TO_NAME,

  // Date conversions
  convertDateToAtlasFormat,
  convertTimestampToAtlasFormat,
  formatAtlasDateForDisplay,
  formatAtlasTimestampForDisplay,

  // Field detection
  detectFieldType,
  isCountryField,
  isDateField,
  isTimestampField,

  // Master functions
  convertToBackendValue,
  convertToDisplayValue,
  convertDataForBackend,
};
